import { api } from '@/util/api'
import {
  parseAthleteMatches,
  parseAthleteProfile,
  parseStandings,
  standingsSearch,
  type AthleteMatch,
  type AthleteProfile,
  type Standings,
  type StandingsQuery,
} from './broadcastData'
import { getJson, serverAt } from './eventAthletes'
import { graphicBody, type GraphicSlot, type StageGraphicInput } from './graphics'
import { noteServerTime } from './serverClock'
import type {
  CalledCamera,
  ChatGif,
  ChatMessage,
  ReactionKind,
  StageBan,
  StageBanKind,
  StageInfo,
  StageMemberPayload,
  StagePayload,
  WatchToken,
} from './types'

const path = encodeURIComponent

/** Why a seat goes back other than the person leaving: `undo`, a guest's join that didn't get them on stage. */
export type LeaveReason = 'undo'

/**
 * The watch screen's endpoints (docs/features/watch-experience.md). The shared
 * client adds the session Bearer token and resolves to the response body, so
 * each call resolves to `{ data }`; 204 answers resolve to nothing.
 */
export const watchApi = {
  /**
   * The owner's stage, or with `handle` that person's stage on the event (404 `stage_not_found` for a handle with none),
   * with what the signed-in viewer may do on it.
   */
  stage: (eventId: string, handle?: string | null) =>
    handle
      ? api.get<{ data: StagePayload }>(`/public/events/${path(eventId)}/stage`, { handle })
      : api.get<{ data: StagePayload }>(`/public/events/${path(eventId)}/stage`),
  /** Every stage on the event for the switcher, the owner's first, then people's oldest first. No viewer. */
  stages: (eventId: string) => api.get<{ data: { stages: StageInfo[] } }>(`/public/events/${path(eventId)}/stages`),
  watchToken: (eventId: string) => api.post<{ data: WatchToken }>(`/public/events/${path(eventId)}/watch_token`),
  /** Says which host this connection watches, with the watch token it joined on (`useWatchingStage`). */
  watching: (stageId: string, token: string) => api.put<void>(`/public/stages/${path(stageId)}/watching`, { token }),
  chatHistory: (stageId: string, before?: string) =>
    api.get<{ data: ChatMessage[] }>(`/public/stages/${path(stageId)}/chat_messages`, { limit: 100, before }),
  /** A message, a GIF (by its GIPHY id: the API looks it up and stores it), or both. */
  postChat: (stageId: string, body: string, gifId?: string) =>
    api.post<{ data: ChatMessage }>(`/stages/${path(stageId)}/chat_messages`, gifId ? { body, gif_id: gifId } : { body }),
  likeChat: (stageId: string, messageId: string) =>
    api.put<{ data: ChatLike }>(`/stages/${path(stageId)}/chat_messages/${path(messageId)}/like`),
  unlikeChat: (stageId: string, messageId: string) =>
    api.delete<{ data: ChatLike }>(`/stages/${path(stageId)}/chat_messages/${path(messageId)}/like`),
  /** Says this viewer is typing; the API passes it on in the stage's next pulse. */
  typing: (stageId: string) => api.post<void>(`/stages/${path(stageId)}/typing`),
  /** `count` taps of one reaction, batched by the bar; anyone watching, signed in or not. */
  react: (stageId: string, kind: ReactionKind, count = 1) =>
    api.post<void>(`/public/stages/${path(stageId)}/reactions`, count > 1 ? { kind, count } : { kind }),
  /** GIPHY through the API (its key stays there): trending with a blank `q`. */
  gifs: (q: string, offset = 0) => api.get<{ data: ChatGif[]; next_offset: number | null }>(`/gifs`, { q, offset }),
  deleteChat: (stageId: string, messageId: string) => api.delete<void>(`/stages/${path(stageId)}/chat_messages/${path(messageId)}`),
  ban: (stageId: string, userId: string, kind: StageBanKind) =>
    api.post<{ data: StageBan }>(`/stages/${path(stageId)}/bans`, { user_id: userId, kind }),
  unban: (stageId: string, userId: string) => api.delete<void>(`/stages/${path(stageId)}/bans/${path(userId)}`),
  goLive: (stageId: string, identity: string) => api.post<{ data: StageMemberPayload }>(`/stages/${path(stageId)}/live`, { identity }),
  /**
   * Studio mode on or off for this connection's host seat: the host's camera shows as a 16:9 source rather than a face.
   * 409 `not_live` when the connection holds no host seat there; turning it off takes back a call on the camera.
   */
  setStudio: (stageId: string, identity: string, studio: boolean) =>
    api.patch<{ data: StageMemberPayload }>(`/stages/${path(stageId)}/live`, { identity, studio }),
  /**
   * Frees this connection's seat. `identity` names the connection leaving; the backend doesn't read it yet (see the web
   * notes' follow-ups). `reason: 'undo'` gives a guest's seat back after a join that failed or was cancelled, keeping
   * their invitation and posting no left card (a backend ask; until it's honoured, it is a plain leave).
   */
  leave: (stageId: string, identity?: string, reason?: LeaveReason) => {
    const query = [identity && `identity=${path(identity)}`, reason && `reason=${path(reason)}`].filter(Boolean).join('&')
    return api.delete<void>(`/stages/${path(stageId)}/live${query ? `?${query}` : ''}`)
  },
  // The client has no put helper; Rails routes PATCH and PUT on this singular resource to the same update.
  callCamera: (stageId: string, identity: string | null) =>
    api.patch<{ data: CalledCamera }>(`/stages/${path(stageId)}/called_camera`, { participant_identity: identity }),
  /** How loud the source under the faces plays for the stage's audience, 0–100 (docs/features/stage-volume.md). */
  setSourceVolume: (stageId: string, level: number) =>
    api.patch<{ data: { stage_id: string; source_volume: number } }>(`/stages/${path(stageId)}/source_volume`, { level }),

  // ── Broadcast elements (docs/features/badger-cohost.md) ──
  // The reads are public and cached per event (no credentials, so every viewer shares the caches): each resolves to
  // `{ data }` already read defensively (`broadcastData.ts`), and rejects with a `PublicReadError` for an answer that
  // isn't 2xx (a 404 is an athlete or race the page can't show).

  /** A race's order from live trackers: finishers first, then by progress. Without a route, the event's main course. */
  standings: async (eventId: string, query: StandingsQuery, signal?: AbortSignal): Promise<{ data: Standings | null }> => {
    const search = standingsSearch(query)
    const body = await getJson(`public/events/${path(eventId)}/standings${search ? `?${search}` : ''}`, signal)
    noteServerTime(serverAt(body))
    return { data: parseStandings(body) }
  },
  /** One athlete: where, how far along their race, in what place. */
  athleteProfile: async (eventId: string, athleteId: string, signal?: AbortSignal): Promise<{ data: AthleteProfile | null }> => ({
    data: parseAthleteProfile(await getJson(`public/events/${path(eventId)}/athletes/${path(athleteId)}`, signal)),
  }),
  /** Fuzzy search by name or bib, closest first, at most 10. */
  athleteSearch: async (eventId: string, q: string, signal?: AbortSignal): Promise<{ data: AthleteMatch[] }> => ({
    data: parseAthleteMatches(await getJson(`public/events/${path(eventId)}/athletes?${new URLSearchParams({ q })}`, signal)),
  }),
  /**
   * Puts a graphic in a slot on the stage, for its whole audience (a host live on it; 403 otherwise, 422 for a spec the
   * API refuses, 429 past 30 a minute). Resolves to the stored spec with its `shown_at`.
   */
  showGraphic: (stageId: string, slot: GraphicSlot, graphic: StageGraphicInput) =>
    api.put<{ data: StageGraphicAnswer }>(`/stages/${path(stageId)}/graphics/${path(slot)}`, { graphic: graphicBody(graphic) }),
  /** Clears a slot. */
  clearGraphic: (stageId: string, slot: GraphicSlot) => api.delete<{ data: StageGraphicAnswer }>(`/stages/${path(stageId)}/graphics/${path(slot)}`),

  // ── Badger (docs/features/badger-cohost.md, Phase 2 contract) ──

  /**
   * Asks Badger onto the stage, by a host live on it on this connection (`identity`, this tab's): the API records who
   * asked and dispatches him. Asking again while he's asked for answers what's there. 403 not a host, 422 not live here
   * on that identity, 503 the dispatch failed (nothing stored), 429 past 30 a minute.
   */
  bringCohost: (stageId: string, identity: string) => api.post<{ data: StageCohostAnswer }>(`/stages/${path(stageId)}/cohost`, { identity }),
  /** Sends Badger off the stage: the request is cleared and his participant removed (best effort). */
  sendOffCohost: (stageId: string) => api.delete<{ data: StageCohostAnswer }>(`/stages/${path(stageId)}/cohost`),
}

/** What the cohost endpoints answer: the stage's request as stored, or null once he's sent off. */
/** A like or unlike as the API answers it: the message's count now and whether this viewer likes it. */
export interface ChatLike {
  id: string
  likes: number
  liked: boolean
}

export interface StageCohostAnswer {
  stage_id: string
  cohost: { invited_by: string; since: string } | null
}

/** What the graphics endpoints answer: the slot's spec as stored (with `shown_at`), or null once cleared. */
export interface StageGraphicAnswer {
  stage_id: string
  slot: GraphicSlot
  graphic: Record<string, unknown> | null
}

